import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useSettledValue } from 'lib/hooks/use-settled-value';
import {
  faultResultsRequestedAtom,
  refineViewportAtom,
  sectionCutsWithoutCollectionAtom,
  sectionGridIdsAtom,
  sectionModeAtom,
  sectionGenerationAtom,
  sectionOrderingAtom,
  sectionVerticesAtom,
  sectionWellsAtom,
  sectionWellWorkspaceIdAtom,
  structureGridCollectionIdAtom,
  wellResultsRequestedAtom,
} from './atoms';
import type { CrossSectionRequest, SectionObjectType } from './types';

/**
 * How long the plane has to hold still before it is cut. Long enough that clicking
 * out a line point by point is one request rather than one per click, short enough
 * that a finished line does not feel held back.
 */
const SETTLE_MS = 300;

/**
 * What every cut asks for, whatever is on display. Ground is always sampled because TVD
 * is measured from it, and asking up front keeps a display toggle from refetching.
 * Plural out, singular back: the response tags one object at a time.
 */
const ALWAYS: SectionObjectType[] = [
  'horizons',
  'ground_surface',
  // County lines are context the plot always shows, like the ground: the mock draws
  // them without asking, so they are requested up front rather than behind a switch.
  'cultural_boundaries',
];

export interface CrossSectionRequestState {
  /** The cut to ask for, once the plane has settled. Null when there is nothing. */
  request: CrossSectionRequest | null;
  /**
   * Which section the settled request belongs to — see `sectionGenerationAtom`. Settled
   * with the request, because right after a clear the generation has moved on while the
   * request still describes the old section, and comparing the two needs a matched pair.
   */
  generation: number;
  /**
   * True while the plane has changed but the request has not caught up — the plot
   * is about to be recalculated, so it should say so rather than showing the
   * instruction that belongs to a half-drawn line.
   */
  settling: boolean;
}

/**
 * The cut the current section state asks for.
 *
 * Everything that changes what the section IS lives here — the plane, the stack, the
 * collection. Everything that changes how it LOOKS is deliberately absent, so a fill
 * toggle or a unit switch cannot reach the network.
 *
 * @return {CrossSectionRequestState} The settled request, and whether it is behind.
 */
export const useCrossSectionRequest = (): CrossSectionRequestState => {
  const mode = useAtomValue(sectionModeAtom);
  const vertices = useAtomValue(sectionVerticesAtom);
  const wells = useAtomValue(sectionWellsAtom);
  const ordering = useAtomValue(sectionOrderingAtom);
  const collectionId = useAtomValue(structureGridCollectionIdAtom);
  const gridIds = useAtomValue(sectionGridIdsAtom);
  const viewport = useAtomValue(refineViewportAtom);
  const wellsRequested = useAtomValue(wellResultsRequestedAtom);
  const faultsRequested = useAtomValue(faultResultsRequestedAtom);
  const generation = useAtomValue(sectionGenerationAtom);
  const wellWorkspaceId = useAtomValue(sectionWellWorkspaceIdAtom);
  const cutsWithoutCollection = useAtomValue(sectionCutsWithoutCollectionAtom);

  const live = useMemo<{
    request: CrossSectionRequest | null;
    generation: number;
  }>(() => {
    if (mode === null || (collectionId === null && !cutsWithoutCollection)) {
      return { request: null, generation };
    }
    const wellTypes: SectionObjectType[] = wellsRequested ? ['wells'] : [];
    const request: CrossSectionRequest = {
      mode,
      // No collection means no horizons to sample and no grids to name: the cut
      // carries the ground and the wells alone, which is the geometry sheet's case.
      ...(collectionId === null
        ? {}
        : { structureGridCollectionId: collectionId, gridIds }),
      // Each mode carries only its own geometry: the backend refuses a request
      // that brings both.
      ...(mode === 'arbitrary_line'
        ? { vertices }
        : { wellUwis: wells.map((well) => well.uwi), ordering }),
      // Present only while the plot is zoomed somewhere worth re-sampling. Held in an
      // atom rather than passed in, so the drawer, the map and the rail all ask for the
      // same cut instead of fetching one each.
      ...(viewport === null ? {} : { viewport }),
      // The geometry sheet's own Well Workspace, whose unpromoted edits join the cut
      // as crossings of their own. No other section names one.
      ...(wellWorkspaceId === null ? {} : { wellWorkspaceId }),
      // Wells and faults join the cut once asked for and stay for the life of the
      // section: every plane edit recalculates them, and hiding the result never has
      // to ask again.
      objectTypes:
        collectionId === null
          ? ['ground_surface', ...wellTypes]
          : [
              ...ALWAYS,
              ...wellTypes,
              ...(faultsRequested ? (['faults'] as SectionObjectType[]) : []),
            ],
    };
    return { request, generation };
  }, [
    viewport,
    mode,
    collectionId,
    vertices,
    wells,
    ordering,
    gridIds,
    wellsRequested,
    faultsRequested,
    generation,
    wellWorkspaceId,
    cutsWithoutCollection,
  ]);

  const settled = useSettledValue(live, SETTLE_MS);

  return {
    request: settled.request,
    generation: settled.generation,
    settling: settled !== live,
  };
};
